import { useSelector, useDispatch } from 'react-redux'
import { setUser, initialState } from 'store/auth/userSlice'
import { apiSignIn, apiSignOut } from 'services/AuthService'
import { onSignInSuccess, onSignOutSuccess } from 'store/auth/sessionSlice'
import appConfig from 'configs/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'
import { BASE_URL } from 'redux/constants/index'
import {
    buildSubscriptionExpiredPayload,
    getApiData,
    getApiMessage,
    getApiStatus,
    isSubscriptionBlockCode,
    persistSubscriptionExpired,
    clearSubscriptionExpired,
    clearRenewalCheckout,
    getApiCode,
} from 'utils/subscriptionAccess'
import { getAuthenticatedEntryPath } from 'utils/authEntryPath'

export const WEB_APP_ACCESS_DENIED_MESSAGE =
	"You don't have permission to access this application. This portal is for admins and managers only. Sales reps should use the mobile app."

/** Carries access-denied message to the login form after session clear. */
export const LOGIN_ACCESS_ERROR_STORAGE_KEY = 'salesworkx_login_access_error'

function resolveAuthority(resolvedUser) {
	const role = resolvedUser?.role || resolvedUser?.Role || resolvedUser?.authority?.[0] || 'User'
	const roleLower = String(role).toLowerCase().trim()

	if (roleLower === 'admin') {
		return ['admin']
	}
	if (roleLower === 'manager' || roleLower === 'owner') {
		// Company owners use the manager portal
		return ['manager']
	}
	if (Array.isArray(resolvedUser?.authority)) {
		const auth = resolvedUser.authority.map((a) => String(a).toLowerCase())
		if (auth.includes('admin')) return ['admin']
		if (auth.includes('manager') || auth.includes('owner')) return ['manager']
	}
	return ['user']
}

function useAuth() {

    const dispatch = useDispatch()

    const navigate = useNavigate()

	const query = useQuery()

    const { token, signedIn } = useSelector((state) => state.auth.session)

    const signIn = async ({ userName, password }) => {
		try {
			const resp = await apiSignIn({ userName, password })
			const respData = resp?.data

			if (!respData) {
				return {
					status: 'failed',
					message: 'Unable to sign in. Please try again.'
				}
			}

			if (getApiStatus(respData) === false) {
                const code = getApiCode(respData)
                const message = getApiMessage(respData) || 'Unable to sign in. Please try again.'

                if (isSubscriptionBlockCode(code)) {
                    persistSubscriptionExpired(respData, { emailAddress: userName })
                    return {
                        status: 'subscription_expired',
                        message,
                        code,
                        data: buildSubscriptionExpiredPayload(respData, { emailAddress: userName }),
                    }
                }

				return {
					status: 'failed',
					message,
				}
			}

			const innerData = getApiData(respData) || {}
			const authToken = innerData.token || innerData.Token

			if (!authToken) {
				return {
					status: 'failed',
					message: 'Unable to sign in. Please try again.',
				}
			}

			const resolvedUser = innerData.user || innerData.User || {
				email: respData.emailAddress || userName,
				userName: respData.emailAddress || userName,
				role: 'User'
			}

			const authority = resolveAuthority(resolvedUser)
			const canUseWebApp = authority.includes('admin') || authority.includes('manager')

			// Do not create a session for non-admin/manager roles — error stays on login page.
			if (!canUseWebApp) {
				return {
					status: 'failed',
					message: WEB_APP_ACCESS_DENIED_MESSAGE,
				}
			}

			dispatch(onSignInSuccess(authToken))
            clearSubscriptionExpired()
            clearRenewalCheckout()
				
			const company = innerData.company || innerData.Company || resolvedUser.company || resolvedUser.Company
			const subscriptions = company?.subscriptions || company?.Subscriptions || []
			const activeSubscription = subscriptions.find(sub => (sub.isActive ?? sub.IsActive) && (sub.status === 'active' || sub.Status === 'active')) || subscriptions[0]
			const activeSubscriptionFromData = innerData.activeSubscription || innerData.ActiveSubscription
			
			const stripeCustomerId = activeSubscription?.stripeCustomerId 
				|| activeSubscription?.StripeCustomerId
				|| activeSubscriptionFromData?.stripeCustomerId
				|| activeSubscriptionFromData?.StripeCustomerId
				|| company?.subscriptions?.[0]?.stripeCustomerId
				|| company?.Subscriptions?.[0]?.StripeCustomerId
			
			const avatarUrl = resolvedUser.avatarUrl || resolvedUser.AvatarUrl || resolvedUser.avatar || ''
			let avatar = ''
			if (avatarUrl) {
				if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
					avatar = avatarUrl
				} else {
					const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL
					const urlPath = avatarUrl.startsWith('/') ? avatarUrl : `/${avatarUrl}`
					avatar = `${baseUrl}${urlPath}`
				}
			}

			const resolvedCompanyId =
				resolvedUser.companyId ||
				resolvedUser.CompanyId ||
				company?.id ||
				company?.Id ||
				null
			
			const userData = { 
				avatar: avatar, 
				userName: resolvedUser.userName || resolvedUser.UserName || resolvedUser.email || resolvedUser.Email || 'Anonymous', 
				authority: authority, 
				email: resolvedUser.email || resolvedUser.Email || '',
				id: resolvedUser.id || resolvedUser.Id || resolvedUser.userId || resolvedUser.email || resolvedUser.userName || null,
				userId: resolvedUser.id || resolvedUser.Id || resolvedUser.userId || resolvedUser.email || resolvedUser.userName || null,
				companyId: resolvedCompanyId,
				organizationId: resolvedCompanyId,
				stripeCustomerId: stripeCustomerId || null,
				subscription: activeSubscription || activeSubscriptionFromData || null
			}
			dispatch(setUser(userData))
			
			const redirectUrl = query.get(REDIRECT_URL_KEY)
			const entryPath = getAuthenticatedEntryPath(authority)
			
			// Replace login in history so Back does not return to /sign-in while still signed in
			// (that used to bounce managers to admin /dashboard → AccessDenied → forced logout).
			navigate(redirectUrl ? redirectUrl : entryPath, { replace: true })

            return {
                status: 'success',
                message: ''
            }
		} catch (errors) {
			return {
                status: 'failed',
                message: errors?.response?.data?.message || errors?.response?.data?.Message || errors.toString()
            }
		}
    }

    const handleSignOut = ()  => {
		dispatch(onSignOutSuccess())
		dispatch(setUser(initialState))
		navigate(appConfig.unAuthenticatedEntryPath)
	}

    const signOut = async () => {
		try {
			await apiSignOut()
			handleSignOut()
		} catch (errors) {
			handleSignOut()
		}
	}
    
    return {
        authenticated: token && signedIn,
        signIn,
        signOut
    }
}

export default useAuth
